import { SOURCES_SHOWN, SourceLogo, StatusBadge, cn, type Tone } from "../ui";
import { Co } from "./mocks";
import { tile } from "./styles";

const ranked = [
  { company: "Vercel", title: "Design engineer", match: 96 },
  { company: "Ramp", title: "Product designer", match: 89 },
  { company: "Notion", title: "Brand designer", match: 81 },
  { company: "Plaid", title: "UX researcher", match: 72 },
];

export function RankedList() {
  return (
    <div className="flex flex-col gap-2" aria-hidden>
      {ranked.map((r, i) => (
        <div key={r.company} className={cn(tile, "flex items-center gap-3 p-3 pr-4")}>
          <Co name={r.company} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-bold text-ink">{r.title}</p>
            <p className="truncate text-[12px] text-muted">{r.company}</p>
          </div>
          <div className="flex w-16 flex-col items-end gap-1">
            <span className="text-[13px] font-bold text-ink tabular-nums">{r.match}%</span>
            <span className="h-1.5 w-full overflow-hidden rounded-[2px] bg-primary-tint/70">
              <span className={cn("block h-full rounded-[2px]", i === 0 ? "bg-accent" : "bg-primary")} style={{ width: `${r.match}%` }} />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

const feeds = [
  { company: "Stripe", source: "Greenhouse", fresh: 14 },
  { company: "Linear", source: "Ashby", fresh: 3 },
  { company: "Plaid", source: "Lever", fresh: 8 },
];

export function Sources() {
  return (
    <div className="flex flex-col gap-5">
      <div className={cn(tile, "p-1.5")} aria-hidden>
        {feeds.map((f, i) => (
          <div key={f.company} className={cn("flex items-center gap-3 px-3 py-2.5", i > 0 && "border-t border-line")}>
            <Co name={f.company} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-bold text-ink">{f.company}</p>
              <p className="flex items-center gap-1.5 truncate text-[12px] text-muted">
                via <SourceLogo source={f.source} size="xs" className="size-3.5" /> {f.source}
              </p>
            </div>
            <span className="text-[13px] font-bold text-ok-ink tabular-nums">+{f.fresh} today</span>
          </div>
        ))}
      </div>
      <ul className="flex flex-wrap gap-1.5" aria-label="Sources">
        {SOURCES_SHOWN.map((s) => (
          <li key={s} className="inline-flex h-8 items-center rounded-control bg-surface px-2 shadow-card">
            <SourceLogo source={s} size="xs" withName className="text-[13px] text-ink-soft" />
          </li>
        ))}
      </ul>
    </div>
  );
}

const applying: { company: string; title: string; sent: boolean }[] = [
  { company: "Linear", title: "Senior product designer", sent: true },
  { company: "Vercel", title: "Design engineer", sent: true },
  { company: "Ramp", title: "Product designer", sent: false },
  { company: "Figma", title: "Designer, editor", sent: false },
];

export function Sending() {
  return (
    <div className={cn(tile, "p-1.5")} aria-hidden>
      {applying.map((a, i) => (
        <div key={a.company} className={cn("flex items-center gap-3 px-3 py-2.5", i > 0 && "border-t border-line")}>
          <Co name={a.company} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-semibold text-ink">{a.title}</p>
            <p className="truncate text-[12px] text-muted">{a.company}</p>
          </div>
          {a.sent ? (
            <StatusBadge tone="amber" pulse={i === 1}>
              Sending
            </StatusBadge>
          ) : (
            <span className="inline-flex h-8 shrink-0 items-center rounded-control bg-primary px-3.5 text-[13px] font-bold text-on-primary shadow-brand">Apply</span>
          )}
        </div>
      ))}
    </div>
  );
}

function Filled({ label, value, tall = false }: { label: string; value: string; tall?: boolean }) {
  return (
    <div>
      <p className="mb-1 text-[12px] font-semibold text-muted">{label}</p>
      <div className={cn("rounded-control bg-sunk px-3 text-[14px] text-ink ring-1 ring-inset ring-line", tall ? "py-2.5 leading-relaxed" : "flex h-10 items-center")}>
        {value}
      </div>
    </div>
  );
}

export function AnswerForm() {
  return (
    <div className={cn(tile, "p-5")} aria-hidden>
      <div className="mb-4 flex items-center gap-3">
        <Co name="Linear" />
        <p className="text-[15px] font-bold text-ink">Senior product designer</p>
      </div>
      <div className="flex flex-col gap-3">
        <Filled label="Full name" value="Jordan Lee" />
        <Filled label="Email" value="jordan@hey.com" />
        <Filled label="Why Linear?" tall value="I’ve used Linear daily for three years and want to work on the issue view I already live in." />
      </div>
    </div>
  );
}

const statuses: { company: string; title: string; status: string; tone: Tone; pulse?: boolean }[] = [
  { company: "Linear", title: "Senior product designer", status: "Confirmed", tone: "mint" },
  { company: "Vercel", title: "Design engineer", status: "Sent", tone: "brand" },
  { company: "Ramp", title: "Product designer", status: "Applying now", tone: "amber", pulse: true },
  { company: "Figma", title: "Designer, editor", status: "Up next", tone: "gray" },
];

export function LiveStatus() {
  return (
    <div className="flex flex-col gap-2" aria-hidden>
      {statuses.map((s) => (
        <div key={s.company} className={cn(tile, "flex items-center gap-3 p-3 pr-3.5")}>
          <Co name={s.company} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-bold text-ink">{s.title}</p>
            <p className="truncate text-[12px] text-muted">{s.company}</p>
          </div>
          <StatusBadge tone={s.tone} pulse={s.pulse} dot={s.tone === "mint"}>
            {s.status}
          </StatusBadge>
        </div>
      ))}
    </div>
  );
}

const onSite = [
  { company: "Adobe", title: "Staff designer", source: "Workday", applied: true },
  { company: "Bosch", title: "UX designer, connected mobility", source: "SmartRecruiters", applied: false },
  { company: "Salesforce", title: "Lead product designer", source: "Workday", applied: false },
];

export function OnSite() {
  return (
    <div className={cn(tile, "p-1.5")} aria-hidden>
      {onSite.map((j, i) => (
        <div key={j.company} className={cn("flex items-center gap-3 px-3 py-3", i > 0 && "border-t border-line")}>
          <Co name={j.company} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-bold text-ink">{j.title}</p>
            <p className="flex items-center gap-1.5 truncate text-[12px] text-muted">
              {j.company} · <SourceLogo source={j.source} size="xs" className="size-3.5" /> {j.source}
            </p>
          </div>
          {j.applied ? (
            <StatusBadge tone="brand">Applied on site</StatusBadge>
          ) : (
            <span className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-control bg-accent px-3 text-[13px] font-bold text-on-accent">
              Open
              <svg viewBox="0 0 16 16" className="size-3.5" fill="none" aria-hidden>
                <path d="M5 11 11 5M6 5h5v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
